import { useEffect, useState } from 'react';
import React from 'react';
import { SelectOption } from '../../../../../../components/Select';
import { SignableOfferTemplate } from '../../../../../../entities/v1/applicant_tracking/SignableOfferTemplate';
import { AsyncSelect } from '../../../../../../components/Select/async';
import { SignableOfferTemplateService } from '../../../../../../services/v1/applicant_tracking/SignableOfferTemplateService';
import { OfferDocuments } from '../../OfferDocuments';
import { Signatory } from '../../../../../../entities/v1/applicant_tracking/Signatory';
import { SignatoryDropdown } from './SignatoryDropdown';

interface PropTypes {
  offerTemplate?: SignableOfferTemplate;
  signatory?: Signatory;
  setIsValid: (valid: boolean) => void;
  onChangeTemplate: (
    offerTemplate: SignableOfferTemplate,
    signatory: Signatory,
  ) => void;
  onChangeSignatory: (signatory: Signatory) => void;
}

function selectedOption(
  loadedTemplates: SignableOfferTemplate[],
  selectedId: string,
): SelectOption {
  if (selectedId == null) {
    return null;
  }

  const selected = loadedTemplates?.find((i) => i.id.toString() === selectedId);

  if (selected == null) {
    return null;
  }

  return {
    label: selected.name,
    value: selected.id.toString(),
  };
}

export function OfferTemplateDropdown(props: {
  label: string;
  onChange?: (newOption: SelectOption) => void;
  loadOptions: (inputValue: string) => Promise<SelectOption[]>;
  selected: SelectOption;
}) {
  return (
    <div className='d-flex align-items-center gap-2'>
      <label className='w-25 text-nowrap fw-semibold fs-5'>
        {props.label} <span className='ps-1 text-danger'>*</span>
      </label>
      <AsyncSelect
        className='w-100'
        isClearable
        placeholder={`Select ${props.label}`}
        onChange={props.onChange}
        loadOptions={props.loadOptions}
        selected={props.selected}
      />
    </div>
  );
}

export function OfferTemplate(props: PropTypes) {
  const [loadedTemplates, setLoadedTemplates] =
    useState<SignableOfferTemplate[]>(null);

  const loadTemplates = async (): Promise<SelectOption[]> => {
    const response = await SignableOfferTemplateService.list([
      'signatories',
      'signatories.user',
    ]);
    setLoadedTemplates(response.signableOfferTemplates);

    return response.signableOfferTemplates.map((v) => {
      return {
        label: v.name,
        value: v.id.toString(),
      };
    });
  };

  const handleChangeSelected = async (selected: SelectOption) => {
    if (selected == null) {
      return props.onChangeTemplate(null, null);
    }

    const selectedTemplate = await SignableOfferTemplateService.show(
      parseInt(selected.value),
      [
        'signatories',
        'signatories.user',
        'signable_documents',
        'email_template',
      ],
    );

    props.onChangeTemplate(
      selectedTemplate,
      selectedTemplate.signatories?.length === 1
        ? selectedTemplate.signatories[0]
        : null,
    );
  };

  useEffect(() => {
    if (props.offerTemplate == null) {
      props.setIsValid(false);
    } else {
      props.setIsValid(
        props.signatory != null ||
          props.offerTemplate.signatories?.length === 0,
      );
    }
  }, [props.offerTemplate, props.signatory]);

  return (
    <>
      <div className='d-flex flex-column gap-4 w-75'>
        <OfferTemplateDropdown
          label={'Offer Template'}
          loadOptions={loadTemplates}
          onChange={handleChangeSelected}
          selected={selectedOption(
            loadedTemplates,
            props.offerTemplate?.id?.toString(),
          )}
        />
        <SignatoryDropdown
          signatories={props.offerTemplate?.signatories}
          selectedSignatory={props.signatory}
          onChangeSignatory={props.onChangeSignatory}
        />
        <OfferDocuments
          signableDocuments={props.offerTemplate?.signableDocuments}
        />
      </div>
    </>
  );
}
